import React, { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from '@mui/joy';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';

/**
 * ModelSelector - Dropdown-based model selector
 *
 * Props:
 * - models: Array of model objects { id, name, display_name, is_default }
 * - selectedModelId: Currently selected model ID
 * - onSelect: Callback when a model is selected
 * - loading: Whether models are being loaded
 * - disabled: Whether selection is disabled (e.g., agent is busy)
 */
const ModelSelector = ({
  models = [],
  selectedModelId,
  onSelect,
  loading = false,
  disabled = false,
}) => {
  // Ensure models is an array
  const modelList = Array.isArray(models) ? models : [];
  
  // Find default model - prioritize is_default flag, then first active, then first model
  const defaultModel = modelList.find((m) => m.is_default === true && m.is_active === true)
    || modelList.find((m) => m.is_active === true)
    || modelList[0]
    || null;
  
  // Determine which model to display - if selectedModelId is set, find that model; otherwise use default
  let selectedModel = null;
  if (selectedModelId != null) {
    selectedModel = modelList.find((m) => m.id === selectedModelId);
  }
  // Fallback to default model if no selection or selection not found
  if (!selectedModel && defaultModel) {
    selectedModel = defaultModel;
  }
  
  // Debug: if no model selected, show count to verify models are loaded
  const displayName = selectedModel?.display_name 
    || selectedModel?.name 
    || (modelList.length > 0 ? (modelList[0].display_name || modelList[0].name || `Model ${modelList[0].id}`) : 'No models');
  const effectiveModelId = selectedModel?.id ?? null;

  // Auto-select default model when component mounts with models but no selection
  useEffect(() => {
    if (selectedModelId == null && defaultModel != null && onSelect) {
      onSelect(defaultModel.id);
    }
  }, [selectedModelId, defaultModel, onSelect]);

  // Don't render if no models and not loading
  if (!loading && modelList.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 0.5,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyOutlinedIcon
            sx={{
              fontSize: 14,
              color: 'neutral.500',
              flexShrink: 0,
            }}
          />
          <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />
          <Typography level="body-xs" color="neutral">
            Loading models...
          </Typography>
        </Box>
      ) : (
        <Dropdown>
          <MenuButton
            variant="plain"
            color="neutral"
            size="sm"
            disabled={disabled}
            startDecorator={
              <SmartToyOutlinedIcon sx={{ fontSize: 14, color: 'neutral.500' }} />
            }
            endDecorator={
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'neutral.500' }} />
            }
            sx={{
              fontSize: '0.75rem',
              fontWeight: 400,
              minHeight: 28,
              px: 1,
              gap: 0.5,
              '--Button-gap': '4px',
              '&:hover': {
                bgcolor: 'neutral.softHoverBg',
              },
            }}
          >
            {displayName}
          </MenuButton>
          <Menu
            placement="bottom-start"
            size="sm"
            sx={{
              minWidth: 180,
              '--ListItemDecorator-size': '24px',
            }}
          >
            {modelList.map((model) => {
              const isSelected = effectiveModelId === model.id;
              return (
                <MenuItem
                  key={model.id}
                  selected={isSelected}
                  onClick={() => onSelect(model.id)}
                  sx={{
                    fontSize: '0.8rem',
                    py: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1,
                    }}
                  >
                    <Typography
                      level="body-sm"
                      sx={{ fontWeight: isSelected ? 500 : 400 }}
                    >
                      {model.display_name || model.name}
                    </Typography>
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: 16, color: 'primary.500' }} />
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
        </Dropdown>
      )}
    </Box>
  );
};

export default ModelSelector;
